// frontend/src/components/workflows/WorkflowModal.tsx - FIXED VERSION
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { cn } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';

// FIXED: Use entity-specific hooks
import { useGroups } from '@/hooks/useGroups';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { StageEditor } from './StageEditor';
import { WorkflowConstraintsComponent } from './WorkflowConstraints';

// FIXED: Import both API types and workflow component types
import type {
  Workflow as APIWorkflow,
  WorkflowStage as APIWorkflowStage,
  Group,
  Organization,
  NormalizedUser,
  Priority,
  StageRole
} from '@/api/types';

// FIXED: Import workflow component types
import type {
  CreateWorkflowRequest,
  UpdateWorkflowRequest,
  WorkflowConstraints as WorkflowConstraintsType,
  WorkflowStage
} from './types';
import { WorkflowUtils } from './types';

// ===== ERROR BOUNDARY =====

class WorkflowModalErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode; fallback?: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): { hasError: boolean; error: Error } {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('WorkflowModal Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="p-4 text-center">
          <p className="text-red-600">Something went wrong with the workflow modal.</p>
          <button 
            onClick={() => this.setState({ hasError: false })}
            className="mt-2 text-blue-600 underline"
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// ===== INTERFACES =====

interface WorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (workflow: CreateWorkflowRequest | UpdateWorkflowRequest) => Promise<void>;
  workflow?: APIWorkflow; // Use API Workflow type for input
  groups: Group[];
  organizations: Organization[];
  currentUser: NormalizedUser | null;
  isSubmitting?: boolean;
  selectedOrganization?: string; // The currently selected organization from the parent page
}

interface FormData {
  name: string;
  description: string;
  priority: Priority;
  isActive: boolean;
  isTemplate: boolean;
  templateCategory?: 'standard' | 'expedited' | 'review-heavy' | 'simple' | 'custom';
  stages: WorkflowStage[];
  constraints: WorkflowConstraintsType;
  organizationId: string; // For implementors
}

// ===== FORM VALIDATION =====

const validateForm = (data: FormData, currentUser: NormalizedUser | null): { 
  isValid: boolean; 
  errors: Record<string, string>; 
  stageErrors: Record<number, Record<string, string>> 
} => {
  const errors: Record<string, string> = {};
  const stageErrors: Record<number, Record<string, string>> = {};

  // Basic validation
  if (!data.name?.trim()) {
    errors.name = 'Workflow name is required';
  } else if (data.name.length < 3) {
    errors.name = 'Workflow name must be at least 3 characters';
  } else if (data.name.length > 100) {
    errors.name = 'Workflow name must be less than 100 characters';
  }

  if (data.description && data.description.length > 500) {
    errors.description = 'Description must be less than 500 characters';
  }

  // Organization validation for implementors
  if (currentUser?.role === 'implementor' && !data.organizationId) {
    errors.organizationId = 'Organization selection is required';
  }

  // Stage validation (simplified since Sender is auto-managed)
  if (data.stages.length === 0) {
    errors.stages = 'At least one stage is required. The workflow will automatically include a Sender stage at the end.';
  } else if (data.stages.length > 20) {
    errors.stages = 'Maximum 20 stages allowed. Please remove some stages to continue.';
  } else {
    // Verify Sender stage is present and last (should always be true with auto-management)
    const lastStage = data.stages[data.stages.length - 1];
    const senderIndex = data.stages.findIndex(stage => stage.role === 'Sender');

    if (senderIndex === -1) {
      errors.stages = 'Internal error: Sender stage is missing. Please refresh and try again.';
    } else if (lastStage.role !== 'Sender') {
      errors.stages = 'Internal error: Sender stage is not in the final position. Please refresh and try again.';
    } else if (data.stages.filter(stage => stage.role === 'Sender').length > 1) {
      errors.stages = 'Internal error: Multiple Sender stages detected. Please refresh and try again.';
    }

    // Verify first stage is an Editor stage (mandatory for response creation)
    if (data.stages.length > 0 && data.stages[0].role !== 'Editor') {
      errors.stages = 'The first stage must be an Editor stage for response creation. Please ensure an Editor stage is at the beginning.';
    }

    // Note: Simplest valid workflow is Editor → Sender (2 stages)
    // More complex workflows can have any combination of stages in between
  }

  // Validate individual stages
  data.stages.forEach((stage, index) => {
    const stageErrs: Record<string, string> = {};
    const stagePosition = index + 1;

    if (!stage.stageName?.trim()) {
      stageErrs.stageName = `Stage ${stagePosition} name is required. Please enter a descriptive name.`;
    } else if (stage.stageName.length < 2) {
      stageErrs.stageName = `Stage ${stagePosition} name must be at least 2 characters.`;
    } else if (stage.stageName.length > 100) {
      stageErrs.stageName = `Stage ${stagePosition} name is too long (max 100 characters).`;
    }

    if (!stage.role) {
      stageErrs.role = `Stage ${stagePosition} role is required. Select Editor, Commenter, Approver, or Sender.`;
    }

    if (stage.description && stage.description.length > 200) {
      stageErrs.description = `Stage ${stagePosition} description is too long (${stage.description.length}/200 characters).`;
    }

    if (stage.timeLimit && stage.timeLimit.hours < 1) {
      stageErrs.timeLimit = `Stage ${stagePosition} time limit must be at least 1 hour.`;
    } else if (stage.timeLimit && stage.timeLimit.hours > 720) {
      stageErrs.timeLimit = `Stage ${stagePosition} time limit cannot exceed 720 hours (30 days).`;
    }

    if (stage.requiredApprovals && stage.requiredApprovals < 1) {
      stageErrs.requiredApprovals = `Stage ${stagePosition} must require at least 1 approval.`;
    } else if (stage.requiredApprovals && stage.requiredApprovals > 100) {
      stageErrs.requiredApprovals = `Stage ${stagePosition} required approvals cannot exceed 100.`;
    }

    // Validate approval groups for Approver/Sender stages
    // Without this a stage carrying no role at all passed every check here and
    // was rejected only by the server, as an opaque "Validation failed".
    if (!stage.role) {
      stageErrs.role = `Stage ${stagePosition} needs a role (Editor, Commenter, Approver or Sender).`;
    }
    if ((stage.role === 'Approver' || stage.role === 'Sender') && (!stage.approvalGroups || stage.approvalGroups.length === 0)) {
      stageErrs.approvalGroups = `Stage ${stagePosition} (${stage.role}) requires at least one approval group to be selected.`;
    }

    if (Object.keys(stageErrs).length > 0) {
      stageErrors[index] = stageErrs;
    }
  });

  // Check for duplicate stage names with detailed info
  const stageNames = data.stages.map(s => s.stageName?.toLowerCase().trim()).filter(Boolean);
  const duplicateNames = new Set<string>();
  const duplicatePositions: Record<string, number[]> = {};

  stageNames.forEach((name, index) => {
    const firstIndex = stageNames.indexOf(name);
    if (firstIndex !== index) {
      duplicateNames.add(name);
      if (!duplicatePositions[name]) {
        duplicatePositions[name] = [firstIndex + 1];
      }
      duplicatePositions[name].push(index + 1);
    }
  });

  if (duplicateNames.size > 0) {
    const duplicateDetails = Array.from(duplicateNames).map(name => {
      const positions = duplicatePositions[name].join(', ');
      return `"${data.stages.find(s => s.stageName?.toLowerCase().trim() === name)?.stageName}" at positions ${positions}`;
    }).join('; ');
    errors.stages = `Duplicate stage names found: ${duplicateDetails}. Each stage must have a unique name.`;
  }

  return { isValid: Object.keys(errors).length === 0 && Object.keys(stageErrors).length === 0, errors, stageErrors };
};

// ===== UTILITY FUNCTIONS =====

// Create a new stage with defaults - FIXED to include description property
const createNewStage = (order: number, availableGroups: Group[], role: StageRole = 'Approver'): WorkflowStage => {
  return {
    stageName: role === 'Sender' ? 'Send Response' : `Stage ${order + 1}`,
    stageIndex: order,
    role: role,
    description: role === 'Sender' ? 'Send the final response to recipients' : '', // FIXED: Always include description
    mandatory: true,
    approvalGroups: availableGroups.length > 0 ? [availableGroups[0]._id || availableGroups[0].id || ''] : [],
    timeLimit: { hours: 24 },
    allowParallel: false,
    autoAdvance: false,
    requiredApprovals: 1,
    order: order
  };
};

// Create default workflow stages
const createDefaultStages = (availableGroups: Group[]): WorkflowStage[] => {
  return [
    createNewStage(0, availableGroups, 'Editor'),
    createNewStage(1, availableGroups, 'Approver'),
    createNewStage(2, availableGroups, 'Sender')
  ];
};

// ===== MAIN COMPONENT =====

// The API populates stage.approvalGroups as { _id, name, type } objects;
// the form, the validators and the update endpoint all want plain ids.
/**
 * A role for a stage that has none — legacy workflows stored only a name.
 * Same reading a person would make: draft/initiate edits, review/approve
 * approves, sign & send sends.
 */
const inferStageRole = (stageName: string, stage: any): 'Editor' | 'Commenter' | 'Approver' | 'Sender' => {
  const n = String(stageName || '').toLowerCase();
  if (/sign\s*(&|and)?\s*send|dispatch|^send\b|issue/.test(n)) return 'Sender';
  if (/approv|review|verif|concur/.test(n)) return 'Approver';
  if (/draft|initiat|prepar|creat|compil/.test(n)) return 'Editor';
  if (/comment|remark|note/.test(n)) return 'Commenter';
  return (stage?.approvalGroups || []).length > 0 ? 'Approver' : 'Editor';
};

const toGroupIds = (groups: any[] | undefined): string[] =>
  (groups || [])
    .map((g: any) => (g && typeof g === 'object' ? String(g._id || g.id || '') : String(g || '')))
    .filter(Boolean);

export const WorkflowModal: React.FC<WorkflowModalProps> = ({
  isOpen,
  onClose,
  onSave,
  workflow,
  groups,
  organizations,
  currentUser,
  isSubmitting = false,
  selectedOrganization
}) => {
  // Store integration
  const { 
    globalError, 
    setGlobalError, 
    clearGlobalError 
  } = useAppStore();

  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    priority: 'normal',
    isActive: true,
    isTemplate: false,
    templateCategory: 'custom',
    stages: [],
    constraints: {
      maxParallelResponses: 100,
      globalTimeLimit: undefined,
      allowedDepartments: [],
      minimumUserRole: 'contributor'
    },
    organizationId: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [stageErrors, setStageErrors] = useState<Record<number, Record<string, string>>>({});
  const [activeTab, setActiveTab] = useState<'basic' | 'stages' | 'constraints'>('basic');

  // Get default organization for implementors
  const defaultOrganizationId = useMemo(() => {
    if (currentUser?.role === 'implementor') {
      // Use the selected organization from the parent page first
      if (selectedOrganization) {
        return selectedOrganization;
      }
      // Fallback to first organization if no selection
      if (organizations.length > 0) {
        return organizations[0]._id || organizations[0].id || '';
      }
    }
    return '';
  }, [currentUser?.role, organizations, selectedOrganization]);

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen) {
      if (workflow) {
        // Convert API workflow to form data
        let workflowStages: WorkflowStage[] = workflow.stages?.map((stage, index) => ({
          ...stage,
          order: index,
          // Older workflows store the stage as { name, order } with no role.
          // Read those too, and give a role-less stage a sensible starting
          // point so the form opens instead of failing validation on save.
          stageName: stage.stageName || (stage as any).name || `Stage ${index + 1}`,
          role: stage.role || inferStageRole((stage.stageName || (stage as any).name || ''), stage),
          stageIndex: index,
          description: (stage as any).description || '', // FIXED: Safe access with type assertion
          mandatory: stage.mandatory ?? true,
          approvalGroups: toGroupIds(stage.approvalGroups as any),
          timeLimit: stage.timeLimit || { hours: 24 },
          allowParallel: stage.allowParallel ?? false,
          autoAdvance: stage.autoAdvance ?? false,
          requiredApprovals: stage.requiredApprovals || 1
        })) || [];

        // Ensure there's always a Sender stage at the end
        const hasSenderStage = workflowStages.some(stage => stage.role === 'Sender');
        if (!hasSenderStage) {
          workflowStages.push(createNewStage(workflowStages.length, groups, 'Sender'));
        } else {
          // Ensure Sender is at the end
          const senderIndex = workflowStages.findIndex(stage => stage.role === 'Sender');
          if (senderIndex !== workflowStages.length - 1) {
            const senderStage = workflowStages.splice(senderIndex, 1)[0];
            workflowStages.push(senderStage);
            // Re-index stages
            workflowStages = workflowStages.map((stage, index) => ({
              ...stage,
              stageIndex: index,
              order: index
            }));
          }
        }

        setFormData({
          name: workflow.name || '',
          description: workflow.description || '',
          priority: workflow.priority || 'normal',
          isActive: workflow.isActive ?? true,
          isTemplate: workflow.isTemplate ?? false,
          templateCategory: workflow.templateCategory || 'custom',
          stages: workflowStages,
          constraints: workflow.constraints || {
            maxParallelResponses: 100,
            globalTimeLimit: undefined,
            allowedDepartments: [],
            minimumUserRole: 'contributor'
          },
          organizationId: typeof workflow.organization === 'string' 
            ? workflow.organization 
            : (workflow.organization as any)?._id || (workflow.organization as any)?.id || defaultOrganizationId
        });
      } else {
        // Reset form for new workflow with default stages
        setFormData({
          name: '',
          description: '',
          priority: 'normal',
          isActive: true,
          isTemplate: false,
          templateCategory: 'custom',
          stages: createDefaultStages(groups), // Create default 3-stage workflow
          constraints: {
            maxParallelResponses: 100,
            globalTimeLimit: undefined,
            allowedDepartments: [],
            minimumUserRole: 'contributor'
          },
          organizationId: defaultOrganizationId
        });
      }
      setErrors({});
      setStageErrors({});
      clearGlobalError();
      setActiveTab('basic');
    }
  }, [workflow, clearGlobalError, isOpen, defaultOrganizationId, groups]);

  // Constraint management - FIXED: Add proper typing and move before usage
  const handleConstraintUpdate = useCallback((field: keyof WorkflowConstraintsType, value: any) => {
    setFormData(prev => ({
      ...prev,
      constraints: {
        ...prev.constraints,
        [field]: value
      }
    }));
  }, []);

  // Stage management functions
  const handleStageAdd = useCallback(() => {
    setFormData(prev => {
      // Find the Sender stage index
      const senderIndex = prev.stages.findIndex(stage => stage.role === 'Sender');

      // Create new stage at the position before Sender (or at the end if no Sender exists yet)
      const insertPosition = senderIndex >= 0 ? senderIndex : prev.stages.length;
      const newStage = createNewStage(insertPosition, groups, 'Approver');

      // Insert the new stage before the Sender
      const newStages = [...prev.stages];
      newStages.splice(insertPosition, 0, newStage);

      // Re-index all stages
      const reindexedStages = newStages.map((stage, index) => ({
        ...stage,
        stageIndex: index,
        order: index
      }));

      return {
        ...prev,
        stages: reindexedStages
      };
    });
  }, [groups]);

  const handleStageUpdate = useCallback((index: number, updatedStage: Partial<WorkflowStage>) => {
    setFormData(prev => {
      // Prevent changing role of first Editor stage
      if (index === 0 && prev.stages[0].role === 'Editor' && updatedStage.role && updatedStage.role !== 'Editor') {
        setGlobalError('Cannot change the role of the first Editor stage. The first stage must be an Editor for response creation.');
        return prev;
      }

      return {
        ...prev,
        stages: prev.stages.map((stage, i) =>
          i === index ? { ...stage, ...updatedStage } : stage
        )
      };
    });
  }, [setGlobalError]);

  const handleStageRemove = useCallback((index: number) => {
    setFormData(prev => {
      const stageToRemove = prev.stages[index];

      // Prevent deletion of Sender stage
      if (stageToRemove.role === 'Sender') {
        setGlobalError('Cannot delete the Sender stage. Every workflow must have a Sender stage as the final step.');
        return prev;
      }

      // Prevent deletion of first Editor stage
      if (index === 0 && stageToRemove.role === 'Editor') {
        setGlobalError('Cannot delete the first Editor stage. Every workflow must start with an Editor stage for response creation.');
        return prev;
      }

      // Filter out the stage and re-index
      const newStages = prev.stages
        .filter((_, i) => i !== index)
        .map((stage, newIndex) => ({
          ...stage,
          stageIndex: newIndex,
          order: newIndex
        }));

      return {
        ...prev,
        stages: newStages
      };
    });
  }, [setGlobalError]);

  const handleStageMove = useCallback((fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= formData.stages.length) return;

    setFormData(prev => {
      const stageToMove = prev.stages[fromIndex];
      const lastIndex = prev.stages.length - 1;

      // Prevent moving Sender stage away from last position
      if (stageToMove.role === 'Sender' && toIndex !== lastIndex) {
        setGlobalError('The Sender stage must remain as the final stage of the workflow.');
        return prev;
      }

      // Prevent moving first Editor stage away from first position
      if (fromIndex === 0 && stageToMove.role === 'Editor' && toIndex !== 0) {
        setGlobalError('The first Editor stage must remain at the beginning of the workflow for response creation.');
        return prev;
      }

      // Prevent moving any stage to replace the first Editor stage
      if (toIndex === 0 && prev.stages[0].role === 'Editor' && fromIndex !== 0) {
        setGlobalError('Cannot move stages before the first Editor stage. The Editor must be the first stage.');
        return prev;
      }

      // Prevent moving any stage after the Sender stage
      if (toIndex === lastIndex && prev.stages[lastIndex].role === 'Sender') {
        setGlobalError('Cannot move stages after the Sender stage. The Sender must be the final stage.');
        return prev;
      }

      const newStages = [...prev.stages];
      const [movedStage] = newStages.splice(fromIndex, 1);
      newStages.splice(toIndex, 0, movedStage);

      return {
        ...prev,
        stages: newStages.map((stage, index) => ({
          ...stage,
          stageIndex: index,
          order: index
        }))
      };
    });
  }, [formData.stages.length, setGlobalError]);

  // Form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    clearGlobalError();
    
    // Validate form
    const validation = validateForm(formData, currentUser);
    setErrors(validation.errors);
    setStageErrors(validation.stageErrors);
    
    if (!validation.isValid) {
      setActiveTab('stages'); // Switch to stages tab if there are stage errors
      return;
    }

    try {
      // Convert to backend format
      const backendStages = formData.stages.map((stage, index) => ({
        stageName: stage.stageName.trim(),
        stageIndex: index,
        role: stage.role,
        description: stage.description?.trim(),
        mandatory: stage.mandatory,
        approvalGroups: toGroupIds(stage.approvalGroups as any),
        timeLimit: stage.timeLimit,
        allowParallel: stage.allowParallel,
        autoAdvance: stage.autoAdvance,
        requiredApprovals: stage.requiredApprovals
      }));

      const workflowData: CreateWorkflowRequest | UpdateWorkflowRequest = {
        name: formData.name.trim(),
        description: formData.description?.trim() || undefined,
        priority: formData.priority,
        isActive: formData.isActive,
        isTemplate: formData.isTemplate,
        templateCategory: formData.templateCategory,
        stages: backendStages,
        constraints: formData.constraints,
        // Include organizationId for implementors creating new workflows
        ...(currentUser?.role === 'implementor' && !workflow && {
          organizationId: formData.organizationId
        })
      };

      await onSave(workflowData);
      onClose();
    } catch (error: any) {
      console.error('Form submission error:', error);
      setGlobalError(error.message || 'Failed to save workflow');
    }
  }, [formData, currentUser, workflow, onSave, onClose, setGlobalError, clearGlobalError]);

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <WorkflowModalErrorBoundary>
      {/* FIXED: Proper modal overlay and scrolling structure */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl h-full max-h-[95vh] flex flex-col overflow-hidden">
          
          {/* Header - Fixed */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {workflow ? 'Edit Workflow' : 'Create Workflow'}
            </h2>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tab Navigation - Fixed */}
          <div className="border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <nav className="flex space-x-8 px-6" role="tablist" aria-label="Workflow configuration">
              {['basic', 'stages', 'constraints'].map((tab) => {
                const hasBasicErrors = tab === 'basic' && (errors.name || errors.description || errors.organizationId);
                const hasStageErrors = tab === 'stages' && (errors.stages || Object.keys(stageErrors).length > 0);
                const hasConstraintErrors = tab === 'constraints' && (errors.constraints);
                const hasErrors = hasBasicErrors || hasStageErrors || hasConstraintErrors;

                return (
                  <button
                    key={tab}
                    role="tab"
                    id={`tab-${tab}`}
                    aria-selected={activeTab === tab}
                    aria-controls={`tabpanel-${tab}`}
                    onClick={() => setActiveTab(tab as any)}
                    className={cn(
                      'py-4 px-1 border-b-2 font-medium text-sm transition-colors relative',
                      activeTab === tab
                        ? hasErrors
                          ? 'border-red-500 text-red-600 dark:text-red-400'
                          : 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : hasErrors
                          ? 'border-transparent text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300'
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    )}
                  >
                    <span className="flex items-center space-x-2">
                      <span>{tab.charAt(0).toUpperCase() + tab.slice(1)}</span>
                      {hasErrors && (
                        <span className="inline-flex items-center justify-center w-2 h-2 bg-red-500 rounded-full" title="Has errors" />
                      )}
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Form Content - Scrollable */}
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto scrollbar-thin p-6">
              {/* Global Error Display */}
              {globalError && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <h3 className="text-sm font-medium text-red-800 dark:text-red-300">
                        Error saving workflow
                      </h3>
                      <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                        {globalError}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab Content */}
              {activeTab === 'basic' && (
                <div role="tabpanel" id="tabpanel-basic" aria-labelledby="tab-basic" className="space-y-6">
                  {/* Basic Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Workflow Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Workflow Name *
                      </label>
                      <Input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Enter workflow name"
                        error={errors.name} // FIXED: Pass error message string directly
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>
                      )}
                    </div>

                    {/* Priority */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Priority
                      </label>
                      <select
                        value={formData.priority}
                        onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as Priority }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Enter workflow description"
                      rows={3}
                      className={cn(
                        "w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white",
                        errors.description 
                          ? "border-red-300 dark:border-red-600" 
                          : "border-gray-300 dark:border-gray-600"
                      )}
                    />
                    {errors.description && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.description}</p>
                    )}
                  </div>

                  {/* Organization Selection for Implementors */}
                  {currentUser?.role === 'implementor' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Organization *
                      </label>
                      <select
                        value={formData.organizationId}
                        onChange={(e) => setFormData(prev => ({ ...prev, organizationId: e.target.value }))}
                        className={cn(
                          "w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white",
                          errors.organizationId 
                            ? "border-red-300 dark:border-red-600" 
                            : "border-gray-300 dark:border-gray-600"
                        )}
                      >
                        <option value="">Select Organization</option>
                        {organizations.map((org) => (
                          <option key={org._id || org.id} value={org._id || org.id}>
                            {org.name}
                          </option>
                        ))}
                      </select>
                      {errors.organizationId && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.organizationId}</p>
                      )}
                    </div>
                  )}

                  {/* Settings */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.isActive}
                          onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Active</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.isTemplate}
                          onChange={(e) => setFormData(prev => ({ ...prev, isTemplate: e.target.checked }))}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Template</span>
                      </label>
                    </div>

                    {formData.isTemplate && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Template Category
                        </label>
                        <select
                          value={formData.templateCategory}
                          onChange={(e) => setFormData(prev => ({ ...prev, templateCategory: e.target.value as any }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          <option value="standard">Standard</option>
                          <option value="expedited">Expedited</option>
                          <option value="review-heavy">Review Heavy</option>
                          <option value="simple">Simple</option>
                          <option value="custom">Custom</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'stages' && (
                <div role="tabpanel" id="tabpanel-stages" aria-labelledby="tab-stages">
                  {/* Info banner about protected stages */}
                  <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 dark:border-blue-600 rounded-r-lg">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-500 dark:text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          <strong>Protected stages:</strong> Every workflow must start with an <strong>Editor</strong> stage (for response creation) and end with a <strong>Sender</strong> stage (for sending). These stages are locked and cannot be moved or deleted. Add additional Editor, Approver, or Commenter stages in between as needed.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      Workflow Stages ({formData.stages.length})
                    </h3>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleStageAdd}
                      disabled={formData.stages.length >= 20}
                    >
                      Add Stage
                    </Button>
                  </div>

                  {errors.stages && (
                    <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 dark:border-red-600 rounded-r-lg shadow-sm">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-500 dark:text-red-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
                            Stage Configuration Error
                          </h4>
                          <p className="text-sm text-red-700 dark:text-red-400">{errors.stages}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <StageEditor
                    stages={formData.stages}
                    availableGroups={groups}
                    onStageUpdate={handleStageUpdate}
                    onStageAdd={handleStageAdd}
                    onStageRemove={handleStageRemove}
                    onStageMove={handleStageMove}
                    errors={stageErrors}
                  />
                </div>
              )}

              {activeTab === 'constraints' && (
                <div role="tabpanel" id="tabpanel-constraints" aria-labelledby="tab-constraints">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">
                    Workflow Constraints
                  </h3>
                  <WorkflowConstraintsComponent
                    constraints={formData.constraints}
                    onConstraintUpdate={handleConstraintUpdate} // FIXED: Use correct prop name
                    errors={errors}
                  />
                </div>
              )}
            </div>

            {/* Footer - Fixed */}
            <div className="border-t border-gray-200 dark:border-gray-700 p-6 flex items-center justify-end space-x-3 flex-shrink-0">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center space-x-2">
                    <LoadingSpinner size="sm" />
                    <span>Saving...</span>
                  </div>
                ) : (
                  workflow ? 'Update Workflow' : 'Create Workflow'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </WorkflowModalErrorBoundary>
  );
};